/**
 * Resource triggers: start a flow when a resource of one kind is created, updated or deleted and
 * matches the trigger's criteria. They are served at `/resource-trigger/watch`, and listed across
 * organizations for flow-api's triggers page at `/resource-trigger/list`.
 */
import { z } from 'zod';
import { ResourceTriggerCriteriaSchema } from './criteria.js';
/** The changes to a resource a trigger can listen for. */
export const ResourceTriggerEventTypeSchema = z.enum(['created', 'updated', 'deleted']);
export const ResourceTriggerEventType = ResourceTriggerEventTypeSchema.enum;
/** Whether a trigger is listening. A deleted trigger is kept, as `SoftDeleted`. */
export const ResourceTriggerStateSchema = z.enum(['Active', 'Inactive', 'SoftDeleted']);
export const ResourceTriggerState = ResourceTriggerStateSchema.enum;
/** Which definitions of the flow a matching event may run. */
export const ResourceTriggerFlowRunOptionSchema = z.enum(['OnlyPublishedRuns', 'AllRuns']);
export const ResourceTriggerFlowRunOption = ResourceTriggerFlowRunOptionSchema.enum;
/** A trigger as resource-gateway serves it. */
export const ResourceTriggerSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    flowId: z.uuid(),
    resourceKey: z.string(),
    eventType: z.array(ResourceTriggerEventTypeSchema),
    criteria: ResourceTriggerCriteriaSchema,
    contextPaths: z.array(z.string()).nullable(),
    state: ResourceTriggerStateSchema,
    flowRunOption: ResourceTriggerFlowRunOptionSchema,
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
});
/** Path parameters the endpoints addressing one trigger accept. */
export const ResourceTriggerIdParamsSchema = z.object({
    id: z.uuid().describe('The trigger.'),
});
/** Query parameters `GET /resource-trigger/watch` accepts. */
export const ResourceTriggerWatchListQuerySchema = z.object({
    flowId: z.uuid().describe('The flow whose triggers to return.'),
});
/**
 * Body `POST /resource-trigger/watch` accepts. Structural validation only: resource-gateway also
 * checks that the fields the criteria and context paths name exist on the resource, which needs
 * its resource definitions.
 *
 * A trigger cannot be created already deleted, so the state is the two a caller may start in.
 */
export const ResourceTriggerCreateBodySchema = z.object({
    flowId: z.uuid().describe('Flow a matching event starts.'),
    organizationId: z.uuid().describe('Organization whose resources the trigger listens to.'),
    resourceKey: z.string().describe('Which kind of resource to listen to.'),
    eventType: z.array(ResourceTriggerEventTypeSchema).describe('Which changes to listen for.'),
    criteria: ResourceTriggerCriteriaSchema.describe('Which of those events match.').optional(),
    contextPaths: z
        .array(z.string())
        .nullable()
        .describe('Fields of the resource to hand the flow run.')
        .optional(),
    state: z
        .enum([ResourceTriggerState.Active, ResourceTriggerState.Inactive])
        .describe('Whether the trigger starts out listening.')
        .optional(),
    flowRunOption: ResourceTriggerFlowRunOptionSchema.optional(),
});
/**
 * Body `PUT /resource-trigger/watch/{id}` accepts. Neither the flow nor the organization is among
 * it: a trigger that moved would be a different trigger.
 */
export const ResourceTriggerUpdateBodySchema = z.object({
    resourceKey: z.string().optional(),
    eventType: z.array(ResourceTriggerEventTypeSchema).optional(),
    criteria: ResourceTriggerCriteriaSchema.optional(),
    contextPaths: z.array(z.string()).nullable().optional(),
    state: ResourceTriggerStateSchema.optional(),
    flowRunOption: ResourceTriggerFlowRunOptionSchema.optional(),
});
/** Query parameters `GET /resource-trigger/list` accepts. */
export const ResourceTriggerListQuerySchema = z.object({
    organizationId: z
        .uuid()
        .describe('Return only triggers of this organization, within what the caller may see.')
        .optional(),
    limit: z.coerce
        .number()
        .int()
        .positive()
        .describe('How many triggers to return.')
        .optional(),
});
//# sourceMappingURL=resourceTrigger.js.map